import {CalendarStyles} from "@/enums/CalendarStyles";
import {CalendarTypeEnum} from "@/enums/CalendarTypeEnum";
import {CalendarFormatWeek} from "@/enums/CalendarFormatWeek";
import {PrintableCalendarAspectRatios} from "@/enums/PrintableCalendarAspectRatios";
import {toTranslationKey} from "@/utils/stringFormat";
import {EventLocationsMixin} from "@/mixins/EventLocations";
import {PrintableCalendarWeekdays} from "@/consts/PrintableCalendarWeekdays";
import { CalendarExportMode } from '@/enums/CalendarExportMode';

export default {
  name: "PrintableCalendarToolbar",
  mixins: [EventLocationsMixin],
  data() {
    return {
      isMenuOpened: false
    }
  },
  props: {
    isLoadingCalendarPrint: {
      type: Boolean
    },
    printCalendarStyle: {
      type: String
    },
    printCalendarFormat: {
      type: String
    },
    selectedWingsPrintableCalendar: {
      type: Array
    },
    calendarPrintWeekFilter: {
      type: Array
    },
    isFacilityTimeFormatAMPM: {
      type: Boolean
    },
    isDisplayAMPM: {
      type: Boolean
    },
    isMeals: {
      type: Boolean
    },
    isPdfLoading: {
      type: Boolean
    },
    isDatePositionRight: {
      type: Boolean
    },
    disableNavArrowsButtons: {
      type: Boolean
    },
    paperFormat: {
      type: String
    },
    isFacilityUS: {
      type: String
    },
    selectedFacilityWing: {
      type: Array
    },
    isWeek: {
      type: String
    },
    is2Weeks: {
      type: String
    },
    isCompactMode: {
      type: Boolean
    },
    weekdays: {
      type: Array
    },
    selectedFilterParams: {
      type: Number
    },
    isShowEndTime: {
      type: Boolean
    },
    isShowConciergeEvents: {
      type: Boolean
    },
    exportQuality: {
      type: String
    }
  },
  computed: {
    printCalendarStyles() {
      return CalendarStyles;
    },
    typeToLabel() {
      return CalendarTypeEnum;
    },
    exportModeToLabel() {
      return CalendarExportMode;
    },
    calendarFormatWeek() {
      return CalendarFormatWeek;
    },
    calendarAspectRatios() {
      return PrintableCalendarAspectRatios;
    },
    calendarAspectRatiosList() {
      if(this.isFacilityUS) {
        return [
          {
            title: this.$t('PrintableCalendar.Tabloid'),
            value: this.calendarAspectRatios.tabloid
          },
          {
            title: this.$t('PrintableCalendar.Letter'),
            value: this.calendarAspectRatios.letter
          }
        ]
      } else {
        return [
          {
            title: this.$t('PrintableCalendar.A4'),
            value: this.calendarAspectRatios.a4
          },
          {
            title: this.$t('PrintableCalendar.A3'),
            value: this.calendarAspectRatios.a3
          }
        ]
      }
    },
    isMonth() {
      return this.printCalendarFormat === this.typeToLabel.month;
    },
    isDay() {
      return this.printCalendarFormat === this.typeToLabel.day;
    },
    isPrintList() {
      return this.printCalendarStyle === this.printCalendarStyles.list;
    },
    isPrintCalendar() {
      return this.printCalendarStyle === this.printCalendarStyles.calendar;
    },
    wingsPrintableCalendar() {
      return this.$store.state.eventLocationModule.eventWings;
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    printableCalendarWeekdays() {
      return PrintableCalendarWeekdays;
    },
    selectedWeekdays() {
      return this.printableCalendarWeekdays.find(x => x.value.every((value, index) => value === this.weekdays[index]));
    }
  },
  methods: {
    generatePdf() {
      this.$emit('generatePdf');
    },
    routeToEndpoint() {
      this.$emit('routeToEndpoint');
    },
    closePrintableCalendar() {
      this.$emit('closePrintableCalendar')
    },
    prev() {
      this.$emit('prev')
    },
    next() {
      this.$emit('next')
    },
    convertToTranslationKey(text: string) {
      return toTranslationKey(text)
    },
    changeSettingItem() {
      if(this.isPrintCalendar && this.isCompactMode && (this.isWeek || this.is2Weeks)) {
        this.$toast.open({
          type: "error",
          message: this.$t('PrintableCalendar.CompactModeError')
        });
      }
    }
  },
  watch: {
    printCalendarStyle(value) {
      this.$emit('update:printCalendarStyle', value);
    },
    calendarPrintWeekFilter(value) {
      this.$emit('update:calendarPrintWeekFilter', value);
    },
    printCalendarFormat(value) {
      this.$emit('update:printCalendarFormat', value)
    },
    isDisplayAMPM(value) {
      this.$emit('update:isDisplayAMPM', value)
    },
    isMeals(value) {
      this.$emit('update:isMeals', value)
    },
    isDatePositionRight(value) {
      this.$emit('update:isDatePositionRight', value)
    },
    paperFormat(value) {
      this.$emit('update:paperFormat', value)
    },
    selectedWingsIds(value) {
      this.selectedWingsPrintableCalendar = this.wingsPrintableCalendar.reduce((result, wing) => {
        if (value.includes(wing.id)) {
          const levels = this.careLevels.filter(level => level.wingsIds.includes(wing.id)).map(level => ({ id: level.id, name: level.name }));
          const levelsIds = levels.map(level => level.id);
          result.push({
            id: wing.id,
            name: wing.name,
            careLevelsIds: levelsIds,
            careLevels: levels,
            backgroundImage: wing.backgroundImage,
            color: wing.backgroundImage
          });
        }
        return result;
      }, []);

      this.$emit('update:selectedWingsPrintableCalendar', this.selectedWingsPrintableCalendar)
    },
    selectedFacilityWing(value) {
      this.selectedWingsIds = value;
    },
    isCompactMode(value) {
      this.$emit('update:isCompactMode', value);
    },
    weekdays(value) {
      this.$emit('update:weekdays', value);
    },
    isShowEndTime(value) {
      this.$emit('update:isShowEndTime', value);
    },
    isShowConciergeEvents(value) {
      this.isMenuOpened = false;
      this.$emit('update:isShowConciergeEvents', value);
    },
    exportQuality(value) {
      this.$emit('update:exportQuality', value)
    }
  },
}